import type { ReactNode } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { useT } from '@/i18n/I18nProvider'
import { cn, formatDate } from '@/lib/utils'
import { today } from '@/lib/timezone'
import {
  activeFilterCount,
  clearFilters,
  matchPeriod,
  PERIOD_PRESETS,
  periodRange,
  type PeriodPreset,
  type QueueState,
} from '@/lib/submissionQueue'
import type { EnumOption, WorkCenter } from '@/types/api'

/**
 * The filters, in view, as a row of chips.
 *
 * They used to sit behind a "Filteri" button that most people never pressed,
 * so a list narrowed by a link looked like a list that had lost its rows. Now
 * every filter is a chip that is always there: empty, it names what it filters
 * ("Vrsta ▾"); set, it says what it is set to ("Vrsta: Sigurnost") and grows
 * an × to take it off.
 *
 * Each chip is a native `<select>` laid over its own label. On a phone that
 * opens the system picker — the largest, most familiar control there is, and
 * the one that works in gloves — and on a desk it is a dropdown that the
 * keyboard already knows how to drive.
 */
export function FilterBar({
  state,
  onChange,
  types,
  priorities,
  statuses,
  centres,
  isManager,
}: {
  state: QueueState
  onChange: (patch: Partial<QueueState>) => void
  types: EnumOption[]
  priorities: EnumOption[]
  statuses: EnumOption[]
  centres: WorkCenter[]
  isManager: boolean
}) {
  const t = useT()
  const now = today()

  // Plants come from the machines this person can see; one plant needs no chip.
  const plants = [
    ...new Map(
      centres.filter((centre) => centre.plant).map((centre) => [centre.plant!.id, centre.plant!]),
    ).values(),
  ]
  const severalPlants = plants.length > 1

  const period = state.from || state.to ? matchPeriod(state.from, state.to, now) : null
  const periodLabel =
    state.from || state.to
      ? period
        ? t(`submissions.period.${period}`)
        : t('submissions.period.custom', {
            from: state.from ? formatDate(state.from) : '…',
            to: state.to ? formatDate(state.to) : '…',
          })
      : null

  const labelOf = (options: { value: string; label: string }[], value: string) =>
    options.find((option) => option.value === value)?.label ?? value

  return (
    // One scrolling row on a phone, bleeding to both edges, so the filters
    // cost one line of the first screen instead of two; wrapped on wider.
    <div className="-mx-4 -my-1 flex items-center gap-1.5 overflow-x-auto px-4 py-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&>*]:shrink-0">
      <ChipSelect
        name={t('submissions.filter.type')}
        value={state.type}
        valueLabel={state.type ? labelOf(types, state.type) : null}
        onChange={(type) => onChange({ type })}
      >
        {types.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </ChipSelect>

      <ChipSelect
        name={t('submissions.filter.priority')}
        value={state.priority}
        valueLabel={state.priority ? labelOf(priorities, state.priority) : null}
        onChange={(priority) => onChange({ priority })}
      >
        {priorities.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </ChipSelect>

      <ChipSelect
        name={t('submissions.filter.workCenter')}
        value={state.workCenterId}
        valueLabel={
          state.workCenterId
            ? labelOf(
                centres.map((centre) => ({ value: centre.id, label: centre.name })),
                state.workCenterId,
              )
            : null
        }
        onChange={(workCenterId) => onChange({ workCenterId })}
      >
        {severalPlants
          ? plants.map((plant) => (
              <optgroup key={plant.id} label={plant.name}>
                {centres
                  .filter((centre) => centre.plant?.id === plant.id)
                  .map((centre) => (
                    <option key={centre.id} value={centre.id}>
                      {centre.name}
                    </option>
                  ))}
              </optgroup>
            ))
          : centres.map((centre) => (
              <option key={centre.id} value={centre.id}>
                {centre.name}
              </option>
            ))}
      </ChipSelect>

      {(severalPlants || state.plantId) && (
        <ChipSelect
          name={t('submissions.filter.plant')}
          value={state.plantId}
          valueLabel={
            state.plantId
              ? labelOf(plants.map((plant) => ({ value: plant.id, label: plant.name })), state.plantId)
              : null
          }
          onChange={(plantId) => onChange({ plantId })}
        >
          {plants.map((plant) => (
            <option key={plant.id} value={plant.id}>
              {plant.name}
            </option>
          ))}
        </ChipSelect>
      )}

      <ChipSelect
        name={t('submissions.filter.period')}
        value={period ?? (state.from || state.to ? 'custom' : '')}
        valueLabel={periodLabel}
        onChange={(value) =>
          onChange(value ? periodRange(value as PeriodPreset, now) : { from: '', to: '' })
        }
      >
        {PERIOD_PRESETS.map((preset) => (
          <option key={preset} value={preset}>
            {t(`submissions.period.${preset}`)}
          </option>
        ))}
        {/* A range that came in a link and is no preset, shown so the select
            has something to display; not offered as a choice. */}
        {periodLabel && !period && (
          <option value="custom" disabled>
            {periodLabel}
          </option>
        )}
      </ChipSelect>

      <ChipSelect
        name={t('submissions.filter.status')}
        value={state.status}
        valueLabel={state.status ? labelOf(statuses, state.status) : null}
        onChange={(status) => onChange({ status })}
      >
        {statuses.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </ChipSelect>

      {/* A switch rather than a select: it has one value. Managers only,
          like the server's notion of who hands reports to people. */}
      {isManager && (
        <ToggleChip
          pressed={state.unassigned}
          onClick={() => onChange({ unassigned: !state.unassigned })}
        >
          {t('submissions.filter.unassigned')}
        </ToggleChip>
      )}

      {/* Only ever arrives from a link — the bell's "late and yours" — and is
          shown so it can be seen and taken off. The tab says the same thing
          everywhere else. */}
      {state.overdue && state.tab !== 'van-roka' && (
        <ToggleChip pressed onClick={() => onChange({ overdue: false })}>
          {t('submissions.filter.overdue')}
        </ToggleChip>
      )}

      {activeFilterCount(state) > 1 && (
        <button
          type="button"
          // The search box is its own control, with its own ×; this clears
          // the chips.
          onClick={() => onChange({ ...clearFilters(state), search: state.search })}
          className="text-primary min-h-control rounded-xl px-2 text-sm font-medium underline-offset-2 hover:underline focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none"
        >
          {t('submissions.filter.clear')}
        </button>
      )}
    </div>
  )
}

const CHIP =
  'min-h-control inline-flex items-center gap-1 rounded-xl border text-sm transition-colors'

function ChipSelect({
  name,
  value,
  valueLabel,
  onChange,
  children,
}: {
  name: string
  value: string
  valueLabel: string | null
  onChange: (value: string) => void
  children: ReactNode
}) {
  const t = useT()
  const set = Boolean(value)
  const shown = set && valueLabel ? `${name}: ${valueLabel}` : name

  return (
    <span
      className={cn(
        CHIP,
        'focus-within:ring-ring/50 relative focus-within:ring-3',
        set
          ? 'border-primary/40 bg-primary/10 text-foreground'
          : 'border-border bg-background/60 text-foreground/80 hover:bg-muted/60',
      )}
    >
      <span className="pointer-events-none flex items-center gap-1 pl-3 pr-2">
        <span className={cn('max-w-48 truncate', set && 'font-medium')}>{shown}</span>
        {!set && <ChevronDown className="size-3.5 opacity-60" aria-hidden />}
      </span>

      {/* Over the label, invisible, so the tap lands on the real control. */}
      <select
        aria-label={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        <option value="">{name}: —</option>
        {children}
      </select>

      {set && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('submissions.filter.remove', { label: shown })}
          className="relative z-10 -ml-1 flex h-full min-h-control items-center rounded-r-xl px-2 opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      )}
    </span>
  )
}

function ToggleChip({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        CHIP,
        'px-3 focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none',
        pressed
          ? 'border-primary/40 bg-primary/10 font-medium'
          : 'border-border bg-background/60 text-foreground/80 hover:bg-muted/60',
      )}
    >
      {children}
      {pressed && <X className="size-3.5 opacity-70" aria-hidden />}
    </button>
  )
}
